import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, LayoutGrid, List, ChevronDown, User, Plus } from "lucide-react";
import AnalyticsTopBar from "../components/AnalyticsTopBar";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { useBoards } from "../hooks/useBoards";
import { usePinnedBoards } from "../context/PinnedBoardsContext";
import * as analyticsApi from "../api/analyticsApi";
import Icon from "../../../../shared/components/Icon";

// Custom SVG Icons (same as CreateNewDropdown)
const BoardIcon = ({ color = "currentColor", size = 16 }) => (
  <Icon name="dashboard" size={size} color={color} />
);

const InsightsIcon = ({ color = "currentColor", size = 16 }) => (
  <Icon name="analytics" size={size} color={color} />
);

const FunnelsIcon = ({ color = "currentColor", size = 16 }) => (
  <Icon name="funnel" size={size} color={color} />
);

const FlowsIcon = ({ color = "currentColor", size = 16 }) => (
  <Icon name="flows" size={size} color={color} />
);

const RetentionIcon = ({ color = "currentColor", size = 16 }) => (
  <Icon name="retention" size={size} color={color} />
);

// Helper: get initials from creator name (e.g. "Akshit Varsani" -> "AV") or email
const getInitialsFromCreator = (creatorName, creatorEmail) => {
  if (creatorName && typeof creatorName === "string") {
    const parts = creatorName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length >= 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
  }
  if (creatorEmail && typeof creatorEmail === "string") {
    const parts = creatorEmail.split("@")[0].split(".");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return creatorEmail.substring(0, 2).toUpperCase();
  }
  return "?";
};

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

const TYPE_OPTIONS = [
  { id: "boards", label: "Boards", checked: true, icon: BoardIcon },
  { id: "insights", label: "Insights", icon: InsightsIcon },
  { id: "funnels", label: "Funnels", icon: FunnelsIcon },
  { id: "flows", label: "Flows", icon: FlowsIcon },
  { id: "retention", label: "Retention", icon: RetentionIcon },
];

function iconForReportType(typeLabel) {
  const map = { Insight: InsightsIcon, Funnel: FunnelsIcon, Flow: FlowsIcon, Retention: RetentionIcon };
  return map[typeLabel] || InsightsIcon;
}

/** Map card.cardType to filter id (for type filter) */
function cardTypeToFilterId(cardType) {
  const t = (cardType || "").toLowerCase();
  if (t === "insights") return "insights";
  if (t === "funnel") return "funnels";
  if (t === "flow") return "flows";
  if (t === "retention") return "retention";
  return null;
}

/** Map cardType to editor URL type (same as BoardViewPage) */
function editorTypeFromCardType(cardType) {
  const t = (cardType || "insights").toLowerCase();
  if (t === "funnel") return "funnels";
  if (t === "flow") return "flows";
  if (t === "retention") return "retention";
  return t;
}

/** Get numeric team id for the currently selected team (for API calls). */
function getSelectedTeamId() {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem("selectedTeamData") : null;
    if (!raw) return null;
    const data = JSON.parse(raw);
    const id = data?.id ?? data?._id;
    return id != null ? id : null;
  } catch {
    return null;
  }
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const { team } = useParams();
  const { boards, loading, error, loadBoards, deleteBoard } = useBoards();
  const { togglePin, isPinned } = usePinnedBoards();

  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("list"); // grid | list — default list view
  const [typesFilter, setTypesFilter] = useState(() => TYPE_OPTIONS.map((t) => ({ ...t, checked: t.id === "boards" })));
  const [creatorsFilter, setCreatorsFilter] = useState("Created by anyone");
  const [creatorsDropdownOpen, setCreatorsDropdownOpen] = useState(false);
  const creatorsDropdownRef = useRef(null);
  const [allCards, setAllCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const teamId = getSelectedTeamId();

  // Close creators dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (creatorsDropdownRef.current && !creatorsDropdownRef.current.contains(e.target)) {
        setCreatorsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load boards for the selected team (all team members' boards). Only fetch when we have a teamId so we never race with a no-param request.
  useEffect(() => {
    if (teamId == null) return;
    loadBoards({ teamId });
  }, [loadBoards, teamId]);

  // When boards load, fetch each board's cards for the type filter (Insights, Funnels, etc.)
  const loadAllCards = useCallback(async () => {
    if (!boards || boards.length === 0) {
      setAllCards([]);
      return;
    }
    setLoadingCards(true);
    try {
      const results = await Promise.all(
        boards.map(async (b) => {
          try {
            const res = await analyticsApi.fetchBoard(b.id, true);
            const boardData = res?.data || res;
            const cards = boardData?.cards || [];
            return (cards || []).map((card) => ({
              card,
              boardId: b.id,
              boardName: b.name || "Untitled",
            }));
          } catch (e) {
            return [];
          }
        })
      );
      setAllCards(results.flat());
    } catch (err) {
      console.error("Failed to load cards for reports filter:", err);
      setAllCards([]);
    } finally {
      setLoadingCards(false);
    }
  }, [boards]);

  useEffect(() => {
    loadAllCards();
  }, [loadAllCards]);

  const checkedTypeIds = typesFilter.filter((t) => t.checked).map((t) => t.id);
  const searchLower = (search || "").toLowerCase().trim();

  // Unique creators from boards (for dropdown options)
  const creatorOptions = (() => {
    const seen = new Set();
    const list = [];
    boards.forEach((b) => {
      const label = b.creatorName || b.creatorEmail || null;
      if (label && !seen.has(label)) {
        seen.add(label);
        list.push(label);
      }
    });
    return list.sort((a, b) => String(a).localeCompare(String(b)));
  })();

  // Build list from type filter: boards and/or report cards, then apply creator filter
  const filteredItems = (() => {
    const items = [];
    if (checkedTypeIds.length === 0) return items;

    if (checkedTypeIds.includes("boards")) {
      boards
        .filter((board) => !searchLower || (board.name || "").toLowerCase().includes(searchLower))
        .forEach((board) => {
          items.push({
            id: board.id,
            title: board.name,
            type: "board",
            lastEdited: formatRelativeTime(board.updatedAt),
            creatorInitials: getInitialsFromCreator(board.creatorName, board.creatorEmail),
            creatorName: board.creatorName || board.creatorEmail || null,
            cardCount: board.cardCount || 0,
          });
        });
    }

    const reportTypeIds = ["insights", "funnels", "flows", "retention"].filter((id) => checkedTypeIds.includes(id));
    if (reportTypeIds.length > 0 && allCards.length >= 0) {
      allCards.forEach(({ card, boardId, boardName }) => {
        const filterId = cardTypeToFilterId(card?.cardType);
        if (!filterId || !reportTypeIds.includes(filterId)) return;
        const title = card?.title || "Untitled";
        if (searchLower && !title.toLowerCase().includes(searchLower) && !(boardName || "").toLowerCase().includes(searchLower)) return;
        const board = boards.find((b) => b.id === boardId);
        const creatorName = board ? (board.creatorName || board.creatorEmail || null) : null;
        items.push({
          id: `card-${boardId}-${card?.id}`,
          type: "report",
          title,
          lastEdited: card?.updatedAt ? formatRelativeTime(card.updatedAt) : "—",
          boardId,
          boardName,
          cardId: card?.id,
          cardType: card?.cardType,
          editorType: editorTypeFromCardType(card?.cardType),
          typeLabel: filterId === "funnels" ? "Funnel" : filterId === "flows" ? "Flow" : filterId === "retention" ? "Retention" : "Insight",
          creatorName,
        });
      });
    }

    // Apply creator filter
    if (creatorsFilter !== "Created by anyone") {
      return items.filter((item) => item.creatorName === creatorsFilter);
    }
    return items;
  })();

  const breadcrumbs = [];

  const handleItemClick = (item) => {
    if (item.type === "board") {
      navigate(`/${team}/analytics/boards/${item.id}`);
    } else if (item.type === "report") {
      if (item.editorType === "flows") {
        const q = new URLSearchParams();
        if (item.cardId) q.set("cardId", item.cardId);
        const qs = q.toString();
        navigate(`/${team}/analytics/flow/${item.boardId}${qs ? `?${qs}` : ""}`);
      } else {
        const params = new URLSearchParams({ type: item.editorType });
        if (item.cardId) params.set("cardId", item.cardId);
        navigate(`/${team}/analytics/boards/${item.boardId}/editor?${params.toString()}`);
      }
    }
  };

  const handleDeleteBoard = async (boardId, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this board?")) {
      try {
        await deleteBoard(boardId);
      } catch (err) {
        console.error("Failed to delete board:", err);
      }
    }
  };

  const toggleType = (id) => {
    setTypesFilter((prev) =>
      prev.map((t) => (t.id === id ? { ...t, checked: !t.checked } : t))
    );
  };

  return (
    <div 
      className="flex flex-col flex-1 min-h-0" 
      style={{ 
        backgroundColor: "#ffffff",
        fontFamily: "Raleway, sans-serif"
      }}
    >
      <main className="flex-1 overflow-auto flex min-h-0">
        {/* Main content */}
        <div className="flex-1 overflow-auto p-6 min-w-0" style={{ fontFamily: "Raleway, sans-serif" }}>
          {/* Search */}
          <div className="mb-6">
            <div
              className="flex items-center gap-2 max-w-md px-4 py-2.5 border rounded-lg"
              style={{
                backgroundColor: theme.bgRaised,
                borderColor: theme.border,
              }}
            >
              <Search className="w-4 h-4 flex-shrink-0" style={{ color: theme.textMuted }} />
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-0 bg-transparent text-sm outline-none"
                style={{ 
                  color: "#000000",
                  fontFamily: "Raleway, sans-serif"
                }}
              />
            </div>
          </div>

          {/* Empty state */}
          {filteredItems.length === 0 && !loading && !loadingCards && (
            <div className="mb-8 text-center py-12">
              <h2 
                className="text-lg font-semibold mb-2" 
                style={{ 
                  color: "#000000",
                  fontFamily: "Raleway, sans-serif"
                }}
              >
                {checkedTypeIds.length === 0 ? "Select at least one type" : "No boards or reports match"}
              </h2>
              <p 
                className="text-sm" 
                style={{ color: "#666666", fontFamily: "Raleway, sans-serif" }}
              >
                {checkedTypeIds.length === 0
                  ? "Use the Types filter on the right to show Boards, Insights, Funnels, Flows, or Retention."
                  : "Try changing your search or selecting more types."}
              </p>
            </div>
          )}

          {/* Results header */}
          {filteredItems.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-4 mb-4">
                <span 
                  className="text-sm" 
                  style={{ 
                    color: "#666666",
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "14px"
                  }}
                >
                  {filteredItems.length} result{filteredItems.length !== 1 ? "s" : ""}
                </span>
            <div className="flex items-center gap-2">
              <div className="flex border overflow-hidden rounded-lg" style={{ borderColor: theme.border }} role="group" aria-label="View layout">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className="p-2 transition-colors"
                  style={{
                    backgroundColor: viewMode === "grid" ? "#e8eaf0" : theme.bgRaised,
                    color: viewMode === "grid" ? "#000000" : theme.textMuted,
                  }}
                  aria-pressed={viewMode === "grid"}
                  aria-label="Grid view"
                  title="Grid view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className="p-2 transition-colors"
                  style={{
                    backgroundColor: viewMode === "list" ? "#e8eaf0" : theme.bgRaised,
                    color: viewMode === "list" ? "#000000" : theme.textMuted,
                  }}
                  aria-pressed={viewMode === "list"}
                  aria-label="List view"
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

              {/* Results list or grid */}
              <div className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" : "space-y-0"}>
                {loading || loadingCards ? (
                  <div className="text-center py-8 col-span-full">
                    <div 
                      className="text-sm" 
                      style={{ color: "#666666", fontFamily: "Raleway, sans-serif" }}
                    >
                      {loading ? "Loading boards..." : "Loading reports..."}
                    </div>
                  </div>
                ) : error ? (
                  <div className="text-center py-8 col-span-full">
                    <div 
                      className="text-sm" 
                      style={{ color: "#ef4444", fontFamily: "Raleway, sans-serif" }}
                    >
                      Error: {error}
                    </div>
                  </div>
                ) : (
                  filteredItems.map((item) => {
                    if (item.type === "report") {
                      const TypeIcon = iconForReportType(item.typeLabel);
                      if (viewMode === "grid") {
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleItemClick(item)}
                            className="flex flex-col items-stretch p-4 text-left rounded-lg border transition-colors hover:bg-gray-50 cursor-pointer"
                            style={{ backgroundColor: "#ffffff", borderColor: theme.border, color: "#000000" }}
                          >
                            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg mb-3" style={{ backgroundColor: `${theme.textMuted}15` }}>
                              <TypeIcon size={24} color={theme.textMuted} />
                            </div>
                            <div className="text-sm font-medium truncate mb-0.5" style={{ fontFamily: "Raleway, sans-serif", color: "#000000", fontSize: "14px" }}>
                              {item.title}
                            </div>
                            <div className="text-xs truncate mb-1" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                              {item.typeLabel} · {item.boardName}
                            </div>
                            <div className="text-xs" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                              Last edited {item.lastEdited}
                            </div>
                          </button>
                        );
                      }
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleItemClick(item)}
                          className="w-full flex items-center px-4 py-3 text-left transition-colors hover:bg-gray-50 cursor-pointer"
                          style={{ backgroundColor: "transparent", color: "#000000" }}
                        >
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg mr-4" style={{ backgroundColor: `${theme.textMuted}15` }}>
                            <TypeIcon size={24} color={theme.textMuted} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" style={{ fontFamily: "Raleway, sans-serif", color: "#000000", fontSize: "14px" }}>
                              {item.title}
                            </div>
                            <div className="text-xs truncate" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                              {item.typeLabel} · {item.boardName}
                            </div>
                          </div>
                          <div className="text-xs flex-shrink-0 ml-2" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                            Last edited {item.lastEdited}
                          </div>
                        </button>
                      );
                    }
                    const pinned = isPinned(item.id);
                    if (viewMode === "grid") {
                      return (
                        <div
                          key={item.id}
                          className="flex flex-col rounded-lg border p-4 transition-colors hover:bg-gray-50"
                          style={{ backgroundColor: "#ffffff", borderColor: theme.border, color: "#000000" }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${theme.textMuted}15` }}>
                              <BoardIcon size={24} color={theme.textMuted} />
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); togglePin(item.id); }}
                              className="flex-shrink-0 p-1.5 rounded transition-colors"
                              style={{ color: pinned ? "#1d0c46" : theme.textMuted }}
                              title={pinned ? "Unpin from sidebar" : "Pin to sidebar"}
                              aria-label={pinned ? "Unpin" : "Pin"}
                            >
                              <Icon name="sidebar_pinned" size={18} color={pinned ? "#1d0c46" : theme.textMuted} />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleItemClick(item)}
                            className="text-left flex-1 min-w-0"
                          >
                            <div className="text-sm font-medium truncate mb-1" style={{ fontFamily: "Raleway, sans-serif", color: "#000000", fontSize: "14px" }}>
                              {item.title}
                            </div>
                            <div className="text-xs mb-1" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                              Last edited {item.lastEdited}
                            </div>
                            <div className="text-xs" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                              {item.cardCount} card{item.cardCount !== 1 ? "s" : ""}
                            </div>
                            <div
                              className="w-8 h-8 flex items-center justify-center text-xs font-medium flex-shrink-0 rounded-full mt-2"
                              style={{ backgroundColor: "#e8eaf0", color: theme.textPrimary }}
                              title={item.creatorName ? `Created by ${item.creatorName}` : "Creator"}
                            >
                              {item.creatorInitials}
                            </div>
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={item.id}
                        className="w-full flex items-center px-4 py-3 text-left transition-colors hover:bg-gray-50"
                        style={{ backgroundColor: "transparent", color: "#000000" }}
                      >
                        <button
                          type="button"
                          onClick={() => handleItemClick(item)}
                          className="flex items-center flex-1 min-w-0 text-left"
                        >
                          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg mr-4" style={{ backgroundColor: `${theme.textMuted}15` }}>
                            <BoardIcon size={24} color={theme.textMuted} />
                          </div>
                          <div className="text-sm font-medium flex-shrink-0" style={{ fontFamily: "Raleway, sans-serif", color: "#000000", fontSize: "14px" }}>
                            {item.title}
                          </div>
                          <div className="text-xs flex-1 text-center min-w-0" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                            Last edited {item.lastEdited}
                          </div>
                          <div className="text-xs mr-4" style={{ color: "#666666", fontFamily: "Raleway, sans-serif", fontSize: "12px" }}>
                            {item.cardCount} card{item.cardCount !== 1 ? "s" : ""}
                          </div>
                          <div
                            className="w-8 h-8 flex items-center justify-center text-xs font-medium flex-shrink-0 rounded-full"
                            style={{ backgroundColor: "#e8eaf0", color: theme.textPrimary }}
                            title={item.creatorName ? `Created by ${item.creatorName}` : "Creator"}
                          >
                            {item.creatorInitials}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); togglePin(item.id); }}
                          className="flex-shrink-0 p-2 rounded transition-colors"
                          style={{ color: pinned ? "#1d0c46" : theme.textMuted }}
                          title={pinned ? "Unpin from sidebar" : "Pin to sidebar"}
                          aria-label={pinned ? "Unpin" : "Pin"}
                        >
                          <Icon name="sidebar_pinned" size={18} color={pinned ? "#1d0c46" : theme.textMuted} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Right filter panel */}
        <aside
          className="w-64 flex-shrink-0 border-l p-4 overflow-y-auto"
          style={{
            backgroundColor: theme.bgRaised,
            borderColor: theme.border,
            fontFamily: "Raleway, sans-serif"
          }}
        >
          <h3 
            className="text-sm font-semibold mb-4" 
            style={{ 
              color: "#1d0c46",
              fontFamily: "Raleway, sans-serif",
              fontSize: "14px"
            }}
          >
            Filter
          </h3>

          <div className="mb-6" ref={creatorsDropdownRef}>
            <div 
              className="text-xs font-semibold uppercase tracking-wider mb-2" 
              style={{ 
                color: theme.textMuted,
                fontFamily: "Raleway, sans-serif"
              }}
            >
              Creators
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setCreatorsDropdownOpen((open) => !open)}
                className="w-full flex items-center gap-2 px-3 py-2 border rounded-lg text-sm text-left"
                style={{
                  backgroundColor: "#ffffff",
                  borderColor: theme.border,
                  color: "#666666",
                  fontFamily: "Raleway, sans-serif",
                  fontSize: "14px"
                }}
                aria-haspopup="listbox"
                aria-expanded={creatorsDropdownOpen}
                aria-label="Filter by creator"
              >
                <User className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 truncate text-left">{creatorsFilter}</span>
                <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ transform: creatorsDropdownOpen ? "rotate(180deg)" : "none" }} />
              </button>
              {creatorsDropdownOpen && (
                <ul
                  role="listbox"
                  className="absolute z-10 mt-1 w-full py-1 border rounded-lg shadow-lg max-h-48 overflow-auto"
                  style={{
                    backgroundColor: "#ffffff",
                    borderColor: theme.border,
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "14px"
                  }}
                >
                  <li
                    role="option"
                    aria-selected={creatorsFilter === "Created by anyone"}
                    onClick={() => {
                      setCreatorsFilter("Created by anyone");
                      setCreatorsDropdownOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors"
                    style={{
                      color: creatorsFilter === "Created by anyone" ? theme.primary : "#666666",
                      backgroundColor: creatorsFilter === "Created by anyone" ? `${theme.primary}12` : "transparent"
                    }}
                  >
                    <User className="w-4 h-4 flex-shrink-0" />
                    Created by anyone
                  </li>
                  {creatorOptions.map((label) => (
                    <li
                      key={label}
                      role="option"
                      aria-selected={creatorsFilter === label}
                      onClick={() => {
                        setCreatorsFilter(label);
                        setCreatorsDropdownOpen(false);
                      }}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors truncate"
                      style={{
                        color: creatorsFilter === label ? theme.primary : "#666666",
                        backgroundColor: creatorsFilter === label ? `${theme.primary}12` : "transparent"
                      }}
                    >
                      {label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mb-6">
            <div 
              className="text-xs font-semibold uppercase tracking-wider mb-3" 
              style={{ 
                color: theme.textMuted,
                fontFamily: "Raleway, sans-serif"
              }}
            >
              Types
            </div>
            <div className="space-y-2">
              {typesFilter.map((t) => {
                const Icon = t.icon;
                return (
                  <label
                    key={t.id}
                    className="flex items-center gap-3 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={t.checked}
                      onChange={() => toggleType(t.id)}
                      className="w-4 h-4 border-gray-300"
                      style={{ accentColor: "#e8eaf0" }}
                    />
                    {Icon && (
                      <span style={{ color: t.checked ? "#000000" : "#666666" }}>
                        <Icon size={16} />
                      </span>
                    )}
                    <span
                      className="text-sm"
                      style={{
                        color: t.checked ? "#000000" : "#666666",
                        fontFamily: "Raleway, sans-serif",
                        fontSize: "14px"
                      }}
                    >
                      {t.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <div 
              className="text-xs font-semibold uppercase tracking-wider mb-2" 
              style={{ 
                color: theme.textMuted,
                fontFamily: "Raleway, sans-serif"
              }}
            >
              Events
            </div>
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-sm"
              style={{
                borderColor: theme.border,
                color: "#666666",
                backgroundColor: "transparent",
                fontFamily: "Raleway, sans-serif",
                fontSize: "14px"
              }}
            >
              <Plus className="w-4 h-4" />
              Add events
            </button>
          </div>
        </aside>
      </main>
    </div>
  );
}
